
import React, { useEffect, useState } from 'react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Combobox from '../components/ui/Combobox';
import { 
  Plus, 
  Trash2, 
  RefreshCw, 
  Send, 
  Inbox, 
  Users, 
  CheckCircle2, 
  FileText,
  Settings as SettingsIcon,
  Shield,
  Briefcase,
  ListOrdered,
  ToggleLeft,
  FolderOpen
} from 'lucide-react';
import useApi from '../hooks/useApi';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Settings = () => {
  const { put } = useApi();
  const { settings, loading, refreshSettings } = useSettings();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dispatch');
  const [permissionRole, setPermissionRole] = useState('');
  const [rolePageAccess, setRolePageAccess] = useState({});
  const [rolePageAccessDirty, setRolePageAccessDirty] = useState(false);
  const [rolePermissions, setRolePermissions] = useState({});
  const [rolePermissionsDirty, setRolePermissionsDirty] = useState(false);

  const pages = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'efile_my_desk', label: 'e-File: My Desk' },
    { key: 'efile_workspace', label: 'e-File: Workspace' },
    { key: 'record_room', label: 'Record Room (Archives)' },
    { key: 'delegations', label: 'Delegations' },
    { key: 'address_book', label: 'Address Book' },
    { key: 'dispatch_list', label: 'Dispatch: List' },
    { key: 'dispatch_create', label: 'Dispatch: Create' },
    { key: 'dispatch_edit', label: 'Dispatch: Edit' },
    { key: 'dispatch_preview', label: 'Dispatch: Preview' },
    { key: 'dispatch_print', label: 'Dispatch: Print' },
    { key: 'diary_list', label: 'Diary: List' },
    { key: 'diary_create', label: 'Diary: Create' },
    { key: 'diary_edit', label: 'Diary: Edit' },
    { key: 'diary_preview', label: 'Diary: Preview' },
    { key: 'green_sheet', label: 'Green Sheet' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'approvals', label: 'Approvals' },
    { key: 'search', label: 'Search' },
    { key: 'reports', label: 'Reports' },
    { key: 'profile', label: 'Profile' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'user_management', label: 'User Management' },
    { key: 'audit_trail', label: 'Audit Trail' },
    { key: 'system_settings', label: 'System Settings' },
    { key: 'office_management', label: 'Office Management' },
  ];

  const canonicalRole = (r) => {
    const s = String(r || '').trim();
    const n = s.toLowerCase().replace(/\s+/g, ' ');
    if (n === 'dak manager') return 'DAK Manager';
    return s;
  };

  const rolesList = Array.from(new Set([...(settings?.user_roles || []), 'SuperAdmin', 'Admin', 'Officer', 'Clerk', 'DAK Manager'].map(canonicalRole)));

  useEffect(() => {
    const next = settings?.role_page_access && typeof settings.role_page_access === 'object' ? settings.role_page_access : {};
    setRolePageAccess(next);
    setRolePageAccessDirty(false);
  }, [settings?.role_page_access]);

  useEffect(() => {
    const next = settings?.role_permissions && typeof settings.role_permissions === 'object' ? settings.role_permissions : {};
    setRolePermissions(next);
    setRolePermissionsDirty(false);
  }, [settings?.role_permissions]);

  useEffect(() => {
    if (permissionRole && rolesList.includes(permissionRole)) return;
    setPermissionRole(rolesList[0] || '');
  }, [permissionRole, rolesList.join('|')]);

  const handleUpdate = async (key, newValue) => {
    try {
      await put(`/settings/${key}`, { value: newValue });
      refreshSettings(); // Refresh global settings
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const toggleRolePage = (role, pageKey) => {
    setRolePageAccess(prev => {
      const next = { ...(prev || {}) };
      const current = Array.isArray(next[role]) ? next[role] : [];
      const has = current.includes(pageKey);
      let updated = has ? current.filter(k => k !== pageKey) : [...current, pageKey];

      const ensure = (k) => { if (!updated.includes(k)) updated = [...updated, k]; };
      const remove = (k) => { if (updated.includes(k)) updated = updated.filter(x => x !== k); };

      if (['diary_create', 'diary_edit', 'diary_preview'].includes(pageKey) && !has) {
        ensure('diary_list');
      }
      if (['dispatch_create', 'dispatch_edit', 'dispatch_preview', 'dispatch_print'].includes(pageKey) && !has) {
        ensure('dispatch_list');
      }

      if (pageKey === 'diary_list' && has) {
        remove('diary_create');
        remove('diary_edit');
        remove('diary_preview');
      }
      if (pageKey === 'dispatch_list' && has) {
        remove('dispatch_create');
        remove('dispatch_edit');
        remove('dispatch_preview');
        remove('dispatch_print');
      }

      next[role] = updated;
      return next;
    });
    setRolePageAccessDirty(true);
  };

  const permissionGroups = [
    {
      title: 'Dashboard',
      items: [
        { key: 'dashboard.read', label: 'View Dashboard' }
      ]
    },
    {
      title: 'Diary (Receipt Register)',
      items: [
        { key: 'diary.create', label: 'Create' },
        { key: 'diary.read', label: 'Read/List/View' },
        { key: 'diary.update', label: 'Update' },
        { key: 'diary.delete', label: 'Delete' },
        { key: 'diary.acknowledge', label: 'Acknowledge' },
        { key: 'diary.putInEfile', label: 'Put in e-File' }
      ]
    },
    {
      title: 'Dispatch Register',
      items: [
        { key: 'dispatch.create', label: 'Create' },
        { key: 'dispatch.read', label: 'Read/List/View' },
        { key: 'dispatch.update', label: 'Update' },
        { key: 'dispatch.delete', label: 'Delete' },
        { key: 'dispatch.bulkCreate', label: 'Bulk Create' },
        { key: 'dispatch.createFromEfile', label: 'Create from e-File' }
      ]
    },
    {
      title: 'Compliance',
      items: [
        { key: 'compliance.read', label: 'Read/List' },
        { key: 'compliance.update', label: 'Update Status' },
        { key: 'compliance.logs.read', label: 'Read Logs' },
        { key: 'compliance.logs.create', label: 'Create Log' }
      ]
    },
    {
      title: 'e-File',
      items: [
        { key: 'efile.create', label: 'Create File' },
        { key: 'efile.read', label: 'Read/List/View' },
        { key: 'efile.update', label: 'Update (Subject/Move)' },
        { key: 'efile.delete', label: 'Delete File' },
        { key: 'efile.folder.create', label: 'Folder: Create' },
        { key: 'efile.folder.read', label: 'Folder: Read' },
        { key: 'efile.folder.update', label: 'Folder: Update' },
        { key: 'efile.folder.delete', label: 'Folder: Delete' },
        { key: 'efile.note.create', label: 'Notes: Create' },
        { key: 'efile.forward', label: 'Forward' },
        { key: 'efile.attachDak', label: 'Attach Dak' },
        { key: 'efile.doc.create', label: 'Documents: Upload' },
        { key: 'efile.doc.read', label: 'Documents: Download' },
        { key: 'efile.doc.delete', label: 'Documents: Delete' },
        { key: 'efile.draft.create', label: 'Drafts: Save' },
        { key: 'efile.draft.commit', label: 'Drafts: Commit' },
        { key: 'efile.draft.delete', label: 'Drafts: Delete' },
        { key: 'efile.approveClose', label: 'Approve & Close' },
        { key: 'efile.archive', label: 'Archive' },
        { key: 'efile.flags.create', label: 'Flags: Create' },
        { key: 'efile.flags.delete', label: 'Flags: Delete' },
        { key: 'efile.flags.items.create', label: 'Flag Items: Create' },
        { key: 'efile.flags.items.delete', label: 'Flag Items: Delete' },
        { key: 'efile.links.create', label: 'Links: Create' },
        { key: 'efile.links.delete', label: 'Links: Delete' },
        { key: 'efile.dispatch.create', label: 'Create Dispatch from e-File' }
      ]
    },
    {
      title: 'Users',
      items: [
        { key: 'users.create', label: 'Create' },
        { key: 'users.read', label: 'Read/List' },
        { key: 'users.update', label: 'Update' },
        { key: 'users.setPassword', label: 'Set Password' }
      ]
    },
    {
      title: 'Offices',
      items: [
        { key: 'offices.tree.read', label: 'Read Organization Tree' },
        { key: 'offices.read', label: 'Read/List' },
        { key: 'offices.create', label: 'Create' },
        { key: 'offices.update', label: 'Update' },
        { key: 'offices.delete', label: 'Delete' }
      ]
    },
    {
      title: 'Settings',
      items: [
        { key: 'settings.read', label: 'Read' },
        { key: 'settings.update', label: 'Update' }
      ]
    },
    {
      title: 'Address Book',
      items: [
        { key: 'addressBook.create', label: 'Create' },
        { key: 'addressBook.read', label: 'Read' },
        { key: 'addressBook.update', label: 'Update' },
        { key: 'addressBook.delete', label: 'Delete' }
      ]
    },
    {
      title: 'Audit / Reports / Search',
      items: [
        { key: 'audit.read', label: 'Audit: Read' },
        { key: 'audit.verify', label: 'Audit: Verify' },
        { key: 'reports.read', label: 'Reports: Read' },
        { key: 'search.read', label: 'Search: Read' }
      ]
    },
    {
      title: 'Files / Uploads / PDF / Export',
      items: [
        { key: 'files.read', label: 'Files: Download' },
        { key: 'files.delete', label: 'Files: Delete' },
        { key: 'uploads.create', label: 'Uploads: Create' },
        { key: 'uploads.read', label: 'Uploads: Read History' },
        { key: 'pdf.read', label: 'PDF: Download' },
        { key: 'pdf.bundle', label: 'PDF: Bundle' },
        { key: 'export.read', label: 'Export Data' }
      ]
    },
    {
      title: 'Relations / Movements / Delegations / Notifications',
      items: [
        { key: 'relations.create', label: 'Relations: Create' },
        { key: 'relations.read', label: 'Relations: Read' },
        { key: 'relations.delete', label: 'Relations: Delete' },
        { key: 'movements.read', label: 'Movements: Read' },
        { key: 'movements.create', label: 'Movements: Create' },
        { key: 'delegations.read', label: 'Delegations: Read' },
        { key: 'delegations.create', label: 'Delegations: Create' },
        { key: 'delegations.delete', label: 'Delegations: Delete' },
        { key: 'notifications.read', label: 'Notifications: Read' },
        { key: 'notifications.update', label: 'Notifications: Mark Read' }
      ]
    }
  ];

  const isPermissionChecked = (role, key) => {
    const roleObj = rolePermissions && typeof rolePermissions === 'object' ? rolePermissions[role] : null;
    if (!roleObj || typeof roleObj !== 'object') return true;
    return roleObj[key] !== false;
  };

  const togglePermission = (role, key, nextChecked) => {
    setRolePermissions(prev => {
      const root = prev && typeof prev === 'object' ? prev : {};
      const next = { ...root };
      const currentRoleObj = next[role] && typeof next[role] === 'object' ? { ...next[role] } : {};
      if (nextChecked) {
        delete currentRoleObj[key];
      } else {
        currentRoleObj[key] = false;
      }
      next[role] = currentRoleObj;
      return next;
    });
    setRolePermissionsDirty(true);
  };

  const getRolePermObj = (role) => {
    const root = rolePermissions && typeof rolePermissions === 'object' ? rolePermissions : {};
    const obj = root?.[role];
    return obj && typeof obj === 'object' ? obj : {};
  };

  const allPermissionKeys = permissionGroups.flatMap(g => g.items.map(it => it.key));

  const countBlocked = (role, keys) => {
    const obj = getRolePermObj(role);
    return keys.reduce((acc, k) => acc + (obj?.[k] === false ? 1 : 0), 0);
  };

  const isAllAllowed = (role, keys) => countBlocked(role, keys) === 0;

  const setPermissionsForKeys = (role, keys, allow) => {
    setRolePermissions(prev => {
      const root = prev && typeof prev === 'object' ? prev : {};
      const next = { ...root };
      const currentRoleObj = next[role] && typeof next[role] === 'object' ? { ...next[role] } : {};

      if (allow) {
        keys.forEach(k => { delete currentRoleObj[k]; });
      } else {
        keys.forEach(k => { currentRoleObj[k] = false; });
      }

      next[role] = currentRoleObj;
      return next;
    });
    setRolePermissionsDirty(true);
  };

  const addValue = (key, value) => {
    if (!value.trim()) return;
    const currentValues = settings[key] || [];
    if (currentValues.includes(value.trim())) {
      toast.error('Value already exists');
      return;
    }
    const newValues = [...currentValues, value.trim()];
    handleUpdate(key, newValues);
  };

  const removeValue = (key, valueToRemove) => {
    const currentValues = settings[key] || [];
    const newValues = currentValues.filter(v => v !== valueToRemove);
    handleUpdate(key, newValues);
  };

  const SingleSetting = ({ title, settingKey, type = 'text', options = [], description, icon: Icon }) => {
    const val = settings[settingKey] ?? '';
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition-colors duration-300">
              {Icon && <Icon className="w-5 h-5 text-indigo-600" />}
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-800">{title}</h3>
              {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
            </div>
          </div>
        </div>
        <div className="p-6">
          {type === 'select' ? (
            <Combobox 
              options={options}
              value={String(val)}
              onChange={(val) => handleUpdate(settingKey, val)}
              className="w-full"
            />
          ) : (
            <div className="flex gap-3">
              <Input 
                defaultValue={val} 
                onBlur={(e) => {
                  if (String(e.target.value) !== String(val)) {
                    handleUpdate(settingKey, e.target.value);
                  }
                }}
                className="w-full mb-0"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const SettingSection = ({ title, settingKey, placeholder, icon: Icon, description }) => {
    const [newValue, setNewValue] = useState('');
    const values = settings[settingKey] || [];

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition-colors duration-300">
              {Icon && <Icon className="w-5 h-5 text-indigo-600" />}
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-800">{title}</h3>
              {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
            </div>
          </div>
          <span className="text-xs font-medium px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full">
            {values.length} items
          </span>
        </div>
        
        <div className="p-6">
          <div className="flex gap-3 mb-6">
            <div className="flex-1 relative">
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addValue(settingKey, newValue.trim());
                    setNewValue('');
                  }
                }}
                placeholder={placeholder}
                className="mb-0 w-full transition-all duration-300 focus:ring-2 focus:ring-indigo-100 border-gray-200 hover:border-indigo-300"
              />
            </div>
            <Button 
              onClick={() => {
                addValue(settingKey, newValue.trim());
                setNewValue('');
              }}
              disabled={!newValue.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-200 disabled:shadow-none disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-300 px-6"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add
            </Button>
          </div>

          <div className="min-h-[100px] bg-gray-50/30 rounded-xl p-4 border border-dashed border-gray-200">
            {values.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                <Inbox className="w-8 h-8 mb-2 opacity-20" />
                <p className="text-sm italic">No values defined yet</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {values.map((val, idx) => (
                  <div 
                    key={idx} 
                    className="group/tag flex items-center bg-white border border-gray-200 rounded-lg pl-3 pr-1 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:shadow-md hover:border-indigo-200 hover:text-indigo-700 transition-all duration-200 cursor-default"
                  >
                    <span>{val}</span>
                    <button 
                      onClick={() => removeValue(settingKey, val)}
                      className="ml-2 p-1 rounded-md text-gray-300 hover:bg-red-50 hover:text-red-500 focus:outline-none transition-colors opacity-0 group-hover/tag:opacity-100"
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'dispatch', label: 'Dispatch', icon: Send, desc: 'Manage dispatch dropdowns' },
    { id: 'receipt', label: 'Diary', icon: Inbox, desc: 'Manage diary dropdowns' },
    { id: 'numbering', label: 'Numbering', icon: ListOrdered, desc: 'Configure numbering schemes' },
    { id: 'efile', label: 'e-File', icon: FolderOpen, desc: 'Configure e-File module' },
    { id: 'users', label: 'Users', icon: Users, desc: 'Manage user roles' },
  ];

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-4 border-indigo-100 animate-spin border-t-indigo-600"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto flex flex-col min-h-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">System Settings</h1>
          <p className="text-gray-500 mt-2 text-lg">Manage application configurations and dropdown values</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 flex-1">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-24">
              <div className="p-4 bg-gray-50/50 border-b border-gray-100">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Configuration</span>
              </div>
              <nav className="p-2 space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-100'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <div className="text-left">
                      <div className={activeTab === tab.id ? 'font-semibold' : ''}>{tab.label}</div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {activeTab === 'dispatch' && (
              <div className="space-y-8 animate-fadeIn">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Dispatch Configuration</h2>
                    <p className="text-sm text-gray-500">Customize dropdowns for dispatch register</p>
                  </div>
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <Send className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-6">
                  <SettingSection 
                    title="Priority Levels" 
                    settingKey="dispatch_priorities" 
                    placeholder="e.g. Urgent, Immediate" 
                    icon={CheckCircle2}
                    description="Define urgency levels for dispatch entries"
                  />
                  <SettingSection 
                    title="Dispatch Modes" 
                    settingKey="dispatch_modes" 
                    placeholder="e.g. Courier, Special Messenger" 
                    icon={Briefcase}
                    description="Specify available methods for sending dak"
                  />
                  <SettingSection 
                    title="Status Options" 
                    settingKey="dispatch_statuses" 
                    placeholder="e.g. Delivered, Returned" 
                    icon={RefreshCw}
                    description="Track the lifecycle of dispatched items"
                  />
                  <SettingSection 
                    title="Categories" 
                    settingKey="dispatch_categories" 
                    placeholder="e.g. Confidential, General" 
                    icon={Shield}
                    description="Classify dispatches for better organization"
                  />
                </div>
              </div>
            )}

            {activeTab === 'receipt' && (
              <div className="space-y-8 animate-fadeIn">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Diary Configuration</h2>
                    <p className="text-sm text-gray-500">Customize dropdowns for receipt register</p>
                  </div>
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <Inbox className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <SettingSection 
                    title="Priority Levels" 
                    settingKey="receipt_priorities" 
                    placeholder="e.g. Urgent" 
                    icon={CheckCircle2}
                    description="Define urgency levels for incoming receipts"
                  />
                  <SettingSection 
                    title="Receipt Modes" 
                    settingKey="receipt_modes" 
                    placeholder="e.g. Hand Delivery" 
                    icon={Briefcase}
                    description="Specify how dak was received"
                  />
                  <SettingSection 
                    title="Document Types" 
                    settingKey="receipt_document_types" 
                    placeholder="e.g. Letter" 
                    icon={FileText}
                    description="Document types for diary entries"
                  />
                  <SettingSection 
                    title="Status Options" 
                    settingKey="receipt_statuses" 
                    placeholder="e.g. Pending" 
                    icon={RefreshCw}
                    description="Track progress of received items"
                  />
                  <SettingSection 
                    title="Categories" 
                    settingKey="receipt_categories" 
                    placeholder="e.g. Legal, HR" 
                    icon={Shield}
                    description="Classify receipts for filtering and reporting"
                  />
                </div>
              </div>
            )}

            {activeTab === 'numbering' && (
              <div className="space-y-8 animate-fadeIn">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Numbering Configuration</h2>
                    <p className="text-sm text-gray-500">Configure auto-generation schemes for reference numbers</p>
                  </div>
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <ListOrdered className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div className="col-span-full">
                     <h3 className="text-lg font-semibold text-gray-800 mb-2">Dispatch Numbering</h3>
                  </div>
                  <SingleSetting 
                    title="Dispatch Number Scheme" 
                    settingKey="dispatch_number_scheme" 
                    type="select" 
                    options={[
                      { value: 'auto', label: 'Auto Generate' },
                      { value: 'manual', label: 'Manual Entry' }
                    ]}
                    icon={ListOrdered}
                    description="Choose how dispatch numbers are generated"
                  />
                  <SingleSetting 
                    title="Dispatch Number Format" 
                    settingKey="dispatch_number_format" 
                    icon={SettingsIcon}
                    description="Format: {OFFICE}, {YYYY}, {SEQ} (e.g. DISP/{OFFICE}/{YYYY}/{SEQ})"
                  />
                  
                  <div className="col-span-full mt-4">
                     <h3 className="text-lg font-semibold text-gray-800 mb-2">Receipt (Diary) Numbering</h3>
                  </div>
                  <SingleSetting 
                    title="Receipt Number Scheme" 
                    settingKey="receipt_number_scheme" 
                    type="select" 
                    options={[
                      { value: 'auto', label: 'Auto Generate' },
                      { value: 'manual', label: 'Manual Entry' }
                    ]}
                    icon={ListOrdered}
                    description="Choose how receipt numbers are generated"
                  />
                  <SingleSetting 
                    title="Receipt Number Format" 
                    settingKey="receipt_number_format" 
                    icon={SettingsIcon}
                    description="Format: {OFFICE}, {YYYY}, {SEQ} (e.g. RCPT/{OFFICE}/{YYYY}/{SEQ})"
                  />
                </div>
              </div>
            )}

            {activeTab === 'efile' && (
              <div className="space-y-8 animate-fadeIn">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">e-File Configuration</h2>
                    <p className="text-sm text-gray-500">Enable module and configure numbering</p>
                  </div>
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <FolderOpen className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <SingleSetting
                    title="Enable e-File Module"
                    settingKey="efile_enabled"
                    type="select"
                    options={[
                      { value: '0', label: 'Disabled' },
                      { value: '1', label: 'Enabled' }
                    ]}
                    icon={ToggleLeft}
                    description="Shows e-File pages and sidebar links"
                  />

                  {(settings.efile_enabled === '1' || settings.efile_enabled === 1 || settings.efile_enabled === 'true' || settings.efile_enabled === true) && (
                    <>
                      <SingleSetting
                        title="e-File Number Scheme"
                        settingKey="efile_number_scheme"
                        type="select"
                        options={[
                          { value: 'auto', label: 'Auto Generate' },
                          { value: 'manual', label: 'Manual Entry' }
                        ]}
                        icon={ListOrdered}
                        description="Choose how e-File numbers are generated"
                      />

                      <SingleSetting
                        title="e-File Number Format"
                        settingKey="efile_number_format"
                        icon={SettingsIcon}
                        description="Format: {OFFICE}, {YYYY}, {SEQ} (e.g. WAPDA/FILE/{YYYY}/{SEQ})"
                      />
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-8 animate-fadeIn">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">User Management</h2>
                    <p className="text-sm text-gray-500">Configure user roles and permissions</p>
                  </div>
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <SettingSection 
                    title="User Roles" 
                    settingKey="user_roles" 
                    placeholder="e.g. Auditor, Manager" 
                    icon={Shield}
                    description="Define roles for access control and permissions"
                  />
                  {user?.role === 'SuperAdmin' ? (
                    <>
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition-colors duration-300">
                              <Shield className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <h3 className="text-base font-bold text-gray-800">Page Access Checklist</h3>
                              <p className="text-xs text-gray-500 mt-0.5">Assign / unassign pages to roles</p>
                            </div>
                          </div>
                          <Button
                            variant="secondary"
                            disabled={!rolePageAccessDirty}
                            onClick={async () => {
                              await handleUpdate('role_page_access', rolePageAccess);
                              setRolePageAccessDirty(false);
                            }}
                            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-50"
                          >
                            Save
                          </Button>
                        </div>
                        <div className="p-6 space-y-4">
                          <div className="max-w-sm">
                            <Combobox
                              options={rolesList.map(r => ({ value: r, label: r }))}
                              value={permissionRole}
                              onChange={setPermissionRole}
                              className="w-full"
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {pages.map(p => {
                              const allowed = Array.isArray(rolePageAccess?.[permissionRole]) ? rolePageAccess[permissionRole] : [];
                              const checked = allowed.includes(p.key);
                              return (
                                <label key={p.key} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleRolePage(permissionRole, p.key)}
                                  />
                                  {p.label}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition-colors duration-300">
                              <Shield className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <h3 className="text-base font-bold text-gray-800">CRUD Permissions</h3>
                              <p className="text-xs text-gray-500 mt-0.5">Role-based control for actions (Create / Read / Update / Delete + workflow actions)</p>
                            </div>
                          </div>
                          <Button
                            variant="secondary"
                            disabled={!rolePermissionsDirty}
                            onClick={async () => {
                              await handleUpdate('role_permissions', rolePermissions);
                              setRolePermissionsDirty(false);
                            }}
                            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-50"
                          >
                            Save
                          </Button>
                        </div>
                        <div className="p-6 space-y-6">
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="max-w-sm w-full">
                              <Combobox
                                options={rolesList.map(r => ({ value: r, label: r }))}
                                value={permissionRole}
                                onChange={setPermissionRole}
                                className="w-full"
                              />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <Button
                                variant="secondary"
                                className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm"
                                onClick={() => setPermissionsForKeys(permissionRole, allPermissionKeys, true)}
                              >
                                Allow All
                              </Button>
                              <Button
                                variant="secondary"
                                className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm"
                                onClick={() => setPermissionsForKeys(permissionRole, allPermissionKeys, false)}
                              >
                                Block All
                              </Button>
                              <span className="text-xs text-gray-500 ml-1">
                                Blocked: <span className="font-semibold text-gray-700">{countBlocked(permissionRole, allPermissionKeys)}</span> / {allPermissionKeys.length}
                              </span>
                            </div>
                          </div>

                          <div className="space-y-6">
                            {permissionGroups.map(g => (
                              <div key={g.title} className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
                                <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-800 truncate">{g.title}</div>
                                    <div className="text-xs text-gray-500">
                                      Blocked: <span className="font-semibold text-gray-700">{countBlocked(permissionRole, g.items.map(i => i.key))}</span> / {g.items.length}
                                    </div>
                                  </div>
                                  <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
                                    <input
                                      type="checkbox"
                                      checked={isAllAllowed(permissionRole, g.items.map(i => i.key))}
                                      onChange={(e) => setPermissionsForKeys(permissionRole, g.items.map(i => i.key), e.target.checked)}
                                    />
                                    All rights
                                  </label>
                                </div>
                                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {g.items.map(it => (
                                    <label key={it.key} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={isPermissionChecked(permissionRole, it.key)}
                                        onChange={(e) => togglePermission(permissionRole, it.key, e.target.checked)}
                                      />
                                      {it.label}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-sm text-gray-600">
                      Only SuperAdmin can change page access and CRUD permissions.
                    </div>
                  )}
                  <SettingSection
                    title="Contact Categories"
                    settingKey="contact_categories"
                    placeholder="e.g. Government, Vendor"
                    icon={Briefcase}
                    description="Manage categories used in Address Book contacts"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
